import paper from 'paper';
import { Point } from 'paper';
import PainterService from '@/services/PainterService';

const state = {
    tool: null,
    selectedShapeIds : '',
    zoom: 1,
    center: new Point(500, 500),
    editorError: null,
    user: null,
    initElements: null,
    origin: new Point(0, 0),
    position: null,
    loadingStep: '',
    availableColors: '',
    selectedColors: '',
    mode: 'workshop',
    cleaningReport: {},
    crossingPoints: {},
    displayErrors: null,
    sizeItemId: null,
    multitouch: false,
    mobile: null,
    isDropped: null,
    isSidebarDropped: null,
    isOpenIconsModal: null,
    textFocus: false,
    standaloneMode: false,
    optimisationMode : false,
    svgPattern : null,
    svgName : null,
    toolType: 'add',
    tutoEditor: null,
    toolSelected : false
};

const mutations = {
    'SET_WHITEBOARD_TOOL' (state, tool) {
        state.tool = tool;
        let paperTool = paper.tools.find(t => t.name === tool);
        if (paperTool) {
            paperTool.activate();
        }
    },
    'SET_DROPPED' (state, isDropped) {
        state.isDropped = isDropped;
    },
    'SET_SIDEBAR_DROPPED' (state, isSidebarDropped) {
        state.isSidebarDropped = isSidebarDropped;
    },
    'SET_OPEN_ICONS_MODAL' (state, isOpenIconsModal) {
        state.isOpenIconsModal = isOpenIconsModal;
    },
    'SET_MOBILE_DEVICE' (state, mobile) {
        state.mobile = mobile;
    },
    'SET_SELECTED_SHAPE_IDS'(state, shapeIds) {
        state.selectedShapeIds = shapeIds;
    },
    'SET_ZOOM' (state, zoom) {
        state.zoom = zoom;
        paper.view.zoom = zoom;
    },
    'SET_CENTER' (state, center) {
        state.center = center;
        paper.view.center = center;
    },
    'SET_ZOOM_AND_CENTER'(state, {zoom, center}) {
        state.zoom = zoom;
        paper.view.zoom = zoom;
        state.center = center;
        paper.view.center = center;
    },
    'SET_EDITOR_ERROR'(state, error) {
        state.editorError = error;
    },
    'SET_USER'(state, user) {
        state.user = user;
    },
    'SET_INIT_ELEMENTS'(state, initElements) {
        state.initElements = initElements;
    },
    'SET_ORIGIN'(state, origin) {
        state.origin = origin;
    },
    'SET_POSITION'(state, position) {
        state.position = position;
    },
    'SET_LOADING_STEP'(state, loadingStep) {
        state.loadingStep = loadingStep;
    },
    'SET_AVAILABLE_COLORS'(state, availableColors) {
        state.availableColors = availableColors;
    },
    'SET_SELECTED_COLORS'(state, selectedColors) {
        state.selectedColors = selectedColors;
    },
    'SET_MODE'(state, mode) {
        state.mode = mode;
    },
    'SET_CLEANING_REPORT'(state, report) {
        state.cleaningReport = report;
    },
    'SET_CROSSING_POINTS'(state, crossingPoints) {
        state.crossingPoints = crossingPoints;
    },
    'DISPLAY_ERRORS'(state, show) {
        state.displayErrors = show;
    },
    'SET_SIZE_ITEM_ID'(state, itemId) {
        state.sizeItemId = itemId;
    },
    'SET_MULTITOUCH'(state, multitouch) {
        state.multitouch = multitouch;
    },
    'SET_TEXT_FOCUS'(state, focus) {
        state.textFocus = focus;
    },
    'SET_STANDALONE_MODE'(state, standaloneMode) {
        state.standaloneMode = standaloneMode;
    },
    'SET_OPTIMISATION_MODE'(state,optimisationMode){
        state.optimisationMode = optimisationMode;
    },
    'SET_SVG_PATTERN'(state,svgPattern){
        state.svgPattern = svgPattern;
    },
    'SET_SVG_NAME'(state,svgName){
        state.svgName = svgName;
    },
    'SET_TOOL_TYPE'(state, toolType) {
        state.toolType = toolType;
    },
    'SET_TUTO'(state, tutoEditor) {
        state.tutoEditor = tutoEditor;
    },
    'SET_TOOL_SELECTED'(state, toolSelected) {
        state.toolSelected = toolSelected;
    }
};

const actions = {
    setWhiteboardTool: ({commit}, tool) => commit('SET_WHITEBOARD_TOOL',  tool),
    setSelectedShapeIds: ({commit}, shapeIds) => commit('SET_SELECTED_SHAPE_IDS', shapeIds),
    setMobileDevice: ({commit}, mobile) => commit('SET_MOBILE_DEVICE', mobile),
    setDropped: ({commit}, isDropped) => {
        commit('SET_DROPPED', isDropped);
    },
    setIsSidebarDropped: ({commit}, isSidebarDropped) => {
        commit('SET_SIDEBAR_DROPPED', isSidebarDropped);
    },
    openIconsChoice: ({commit}, isOpenIconsModal) => {
        commit('SET_OPEN_ICONS_MODAL', isOpenIconsModal);
    },
    setSelectedShapes: ({ commit, dispatch }, shapes) => {
        let shapeIds = shapes.map(s => s.id).join(',');
        commit('SET_SELECTED_SHAPE_IDS', shapeIds);
        if (shapes.length == 0) {
            dispatch('setSelectedShape', null);
        }
        if (shapes.length == 1) {
            dispatch('setSelectedShape', shapes[0]);
        }
    },
    setZoom: ({commit, getters}, zoom) => {
        commit('SET_ZOOM', zoom);
        if (getters.selectedShapeIds) {
            let selecter = paper.tools.find(t => t.name === 'selecter');
            selecter.initSelectionRectangle();
        }
        paper.tools.find(t => t.name === "ruler").resizeAll();
        PainterService.afterZoom();
    },
    setZoomCenter: ({ commit, getters}, zoomAndCenter) => {
        commit('SET_ZOOM_AND_CENTER', zoomAndCenter);
        if (getters.selectedShapeIds) {
            let selecter = paper.tools.find(t => t.name === 'selecter');
            selecter.initSelectionRectangle();
        }
        paper.tools.find(t => t.name === "ruler").resizeAll();
        PainterService.afterZoom();
    },
    setCenter: ({commit}, center) => commit('SET_CENTER', center),
    setEditorError: ({ commit }, error) => {
        error.occuredOn = Date.now();
        commit('SET_EDITOR_ERROR', error);
    },
    setUser: ({ commit }, user) => commit('SET_USER', user),
    setInitElements: ({ commit }, initElements) => commit('SET_INIT_ELEMENTS', initElements),
    setOrigin: ({ commit }, origin) => commit('SET_ORIGIN', origin),
    setPosition: ({ commit }, position) => commit('SET_POSITION', position),
    setLoadingStep: ({ commit }, loadingStep) => commit('SET_LOADING_STEP', loadingStep),
    setSelectedColors: ({ commit }, selectedColors) => commit('SET_SELECTED_COLORS', selectedColors),
    setAvailableColors: ({ commit }, availableColors) => {
        commit('SET_AVAILABLE_COLORS', availableColors);
        commit('SET_SELECTED_COLORS', availableColors)
    },
    setMode: ({ commit }, mode) => commit('SET_MODE', mode),
    setCleaningReport: ({ commit }, report) => commit('SET_CLEANING_REPORT', report),
    setCrossingPoints: ({ commit }, crossingPoints) => commit('SET_CROSSING_POINTS', crossingPoints),
    displayErrors: ({ commit }, show) => commit('DISPLAY_ERRORS', show),
    setSizeItemId: ({ commit }, itemId) => commit('SET_SIZE_ITEM_ID', itemId),
    setMultitouch: ({ commit }, multitouch) => commit('SET_MULTITOUCH', multitouch),
    textFocus: ({ commit }) => commit('SET_TEXT_FOCUS', true),
    textBlur: ({ commit }) => commit('SET_TEXT_FOCUS', false),
    setStandaloneMode: ({ commit }, standaloneMode) => commit('SET_STANDALONE_MODE', standaloneMode),
    setOptimisationMode: ({ commit }, optimisationMode) => commit('SET_OPTIMISATION_MODE', optimisationMode),
    setSvgPattern: ({commit}, svgPattern) => {
        commit('SET_SVG_PATTERN', svgPattern);
    },
    setSvgName: ({commit}, svgName) => {
        commit('SET_SVG_NAME', svgName);
    },
    setToolType: ({ commit }, toolType) => commit('SET_TOOL_TYPE', toolType),
    setTuto: ({ commit }, tutoEditor) => commit('SET_TUTO', tutoEditor)
};

const getters = {
    mobile: state => state.mobile,
    isDropped: state => state.isDropped,
    isSidebarDropped: state => state.isSidebarDropped,
    isOpenIconsModal: state => state.isOpenIconsModal,
    tool: state => state.tool,
    selectedShapeIds: state => state.selectedShapeIds ? state.selectedShapeIds.split(',').map(s => parseInt(s)) : [],
    zoom: state => state.zoom,
    center: state => state.center,
    editorError: state => state.editorError,
    user: state => state.user,
    initElements: state => state.initElements,
    origin: state => state.origin,
    position: state => state.position,
    loadingStep: state => state.loadingStep,
    availableColors: state => state.availableColors,
    selectedColors: state => state.selectedColors,
    mode: state => state.mode,
    cleaningReport: state => state.cleaningReport,
    crossingPoints: state => state.crossingPoints,
    displayErrors: state => state.displayErrors,
    sizeItemId: state => state.sizeItemId,
    multitouch: state => state.multitouch,
    textFocus: state => state.textFocus,
    standaloneMode: state => state.standaloneMode,
    svgPattern: state => state.svgPattern,
    svgName: state => state.svgName,
    optimisationMode: state => state.optimisationMode,
    toolType: state => state.toolType,
    tutoEditor: state => state.tutoEditor,
    toolSelected : state => state.toolSelected
};

export default {
    state,
    mutations,
    actions,
    getters
};
